import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { api } from '../lib/api';
import LocationGate from './LocationGate';
import { formatPrice, formatTime, isHappyHourNow, summarizeHappyHour, formatDays, formatTimeRange, tagClass } from '../lib/menu';

const {
  FiSearch, FiSun, FiMoon, FiPhone, FiMapPin, FiShare2,
  FiFilter, FiX, FiClock, FiDownload, FiGlobe, FiRefreshCw
} = FiIcons;

const EMPTY_FILTERS = { category: [], type: [], spirit: [], tag: [], happyHour: false };

function useDarkMode() {
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    // Saved preference wins, otherwise follow the system
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark' || (savedTheme === null && prefersDark)) {
      setDarkMode(true);
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      if (localStorage.getItem('theme') === null) {
        setDarkMode(e.matches);
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  return [darkMode, setDarkMode];
}

function Collapsible({ open, onToggle, icon, title, gradient, badge, children }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden"
    >
      <button
        onClick={onToggle}
        aria-expanded={open}
        className={`w-full px-6 py-4 flex items-center justify-between text-white transition-all ${gradient}`}
      >
        <div className="flex items-center space-x-3 text-left">
          {icon && <span className="text-2xl">{icon}</span>}
          <h2 className="text-xl font-bold">{title}</h2>
          {badge}
        </div>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <SafeIcon icon={FiX} className="w-5 h-5 transform rotate-45" />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
}

function FilterGroup({ label, options, selected, onToggle }) {
  if (!options.length) return null;
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = selected.includes(opt.id);
          return (
            <button
              key={opt.id}
              onClick={() => onToggle(opt.id)}
              className={`px-3 py-2 rounded-full text-sm transition-all ${
                active
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {opt.icon ? `${opt.icon} ` : ''}{opt.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MenuSkeleton() {
  return (
    <div className="space-y-6" aria-busy="true" aria-label="Loading menu">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="h-16 rounded-2xl bg-white/70 dark:bg-gray-800/70 animate-pulse" />
      ))}
    </div>
  );
}

function Menu() {
  const [darkMode, setDarkMode] = useDarkMode();
  const [menu, setMenu] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [gate, setGate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedSections, setExpandedSections] = useState(null);
  const [, setTick] = useState(0);

  const load = () => {
    setLoadError(null);
    api.get('menu')
      .then((data) => {
        setGate(null);
        setMenu(data);
        document.title = `${data.venue?.name || 'The Stonewall Inn'} Interactive Menu`;
      })
      .catch((err) => {
        // Location lock is on and this visitor has no pass yet
        if (err.status === 403 && err.body?.geofence) setGate(err.body.geofence);
        else setLoadError(err.message);
      });
  };

  useEffect(load, []);

  // Re-evaluate "happy hour now" once a minute
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  const venue = menu?.venue || {};
  const lookups = useMemo(() => {
    const index = (rows = []) => new Map(rows.map((r) => [r.id, r]));
    return { types: index(menu?.types), spirits: index(menu?.spirits), tags: index(menu?.tags) };
  }, [menu]);

  // Featured categories start open
  const expanded = expandedSections ?? (menu ? menu.categories.filter((c) => c.is_featured).map((c) => `c${c.id}`) : []);

  const activeHappyHours = (menu?.happyHours || []).filter((hh) => isHappyHourNow(hh, venue.timezone));
  const filterCount = filters.category.length + filters.type.length + filters.spirit.length + filters.tag.length + (filters.happyHour ? 1 : 0);
  const narrowing = filterCount > 0 || searchTerm.trim() !== '';

  const filteredCategories = useMemo(() => {
    if (!menu) return [];
    const q = searchTerm.trim().toLowerCase();
    const matches = (drink) => {
      if (filters.type.length && !filters.type.includes(drink.type_id)) return false;
      if (filters.spirit.length && !drink.spirit_ids.some((id) => filters.spirit.includes(id))) return false;
      if (filters.tag.length && !drink.tag_ids.some((id) => filters.tag.includes(id))) return false;
      if (filters.happyHour && !drink.happy_hour_eligible) return false;
      if (!q) return true;
      const haystack = [
        drink.name,
        drink.description,
        lookups.types.get(drink.type_id)?.name,
        ...drink.spirit_ids.map((id) => lookups.spirits.get(id)?.name),
        ...drink.tag_ids.map((id) => lookups.tags.get(id)?.name),
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    };
    return menu.categories
      .filter((c) => !filters.category.length || filters.category.includes(c.id))
      .map((c) => ({ ...c, drinks: menu.drinks.filter((d) => d.category_id === c.id && matches(d)) }))
      .filter((c) => c.drinks.length > 0);
  }, [menu, searchTerm, filters, lookups]);

  const toggleFilter = (group, id) => {
    setFilters((prev) => ({
      ...prev,
      [group]: prev[group].includes(id) ? prev[group].filter((x) => x !== id) : [...prev[group], id],
    }));
  };

  const isOpen = (key) => narrowing || expanded.includes(key);
  const toggleSection = (key) => {
    setExpandedSections((prev) => {
      const current = prev ?? expanded;
      return current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
    });
  };

  const handleShare = async () => {
    const title = `${venue.name || 'The Stonewall Inn'} - Interactive Menu`;
    if (navigator.share) {
      try {
        await navigator.share({ title, text: `Check out the drink menu at ${venue.name || 'The Stonewall Inn'}!`, url: window.location.href });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const happyHourPill = () => {
    if (!menu?.happyHours?.length) return null;
    if (activeHappyHours.length) {
      const hh = activeHappyHours[0];
      return (
        <button
          onClick={() => setFilters((f) => ({ ...f, happyHour: !f.happyHour }))}
          className="flex items-center space-x-2 px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full text-sm font-medium"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-600" />
          </span>
          <span>{hh.title} now until {formatTime(hh.end_time)}</span>
        </button>
      );
    }
    const hh = menu.happyHours[0];
    return (
      <div className="flex items-center space-x-2 px-3 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 rounded-full text-sm">
        <SafeIcon icon={FiClock} className="w-4 h-4 shrink-0" />
        <span>{hh.title}: {summarizeHappyHour(hh)}</span>
      </div>
    );
  };

  if (gate) return <LocationGate info={gate} onUnlocked={load} />;

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-purple-50 to-pink-50'}`}>
      {/* Sticky Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="sticky top-0 z-50 backdrop-blur-lg bg-white/90 dark:bg-gray-900/90 border-b border-purple-200 dark:border-purple-800"
      >
        <div className="px-4 py-4 max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              {venue.logoUrl && (
                <div className="w-12 h-12 overflow-hidden rounded-full">
                  <img src={venue.logoUrl} alt={`${venue.name} Logo`} className="w-full h-full object-cover" />
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  {venue.name || 'The Stonewall Inn'}
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">{venue.subtitle || 'Drink Menu'}</p>
              </div>
            </div>
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              aria-label="Toggle dark mode"
            >
              <SafeIcon icon={darkMode ? FiSun : FiMoon} className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative mb-4">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="search"
              placeholder="Search drinks, spirits, tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white dark:bg-gray-800 dark:text-white border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
            />
          </div>

          {/* Filter Toggle */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-800 transition-colors"
            >
              <SafeIcon icon={FiFilter} className="w-4 h-4" />
              <span>Filters</span>
              {filterCount > 0 && (
                <span className="bg-purple-500 text-white text-xs px-2 py-1 rounded-full">{filterCount}</span>
              )}
            </button>
            {happyHourPill()}
          </div>

          {/* Filter Pills */}
          <AnimatePresence>
            {showFilters && menu && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="mt-4 overflow-hidden"
              >
                <div className="space-y-4 max-h-[50vh] overflow-y-auto pb-1">
                  {menu.happyHours.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setFilters((f) => ({ ...f, happyHour: !f.happyHour }))}
                        className={`px-3 py-2 rounded-full text-sm transition-all ${
                          filters.happyHour
                            ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white'
                            : 'bg-yellow-100 dark:bg-yellow-900/60 text-yellow-800 dark:text-yellow-200 hover:bg-yellow-200'
                        }`}
                      >
                        🎉 Happy Hour eligible
                      </button>
                    </div>
                  )}
                  <FilterGroup label="Category" options={menu.categories} selected={filters.category} onToggle={(id) => toggleFilter('category', id)} />
                  <FilterGroup label="Drink type" options={menu.types} selected={filters.type} onToggle={(id) => toggleFilter('type', id)} />
                  <FilterGroup label="Alcohol" options={menu.spirits} selected={filters.spirit} onToggle={(id) => toggleFilter('spirit', id)} />
                  <FilterGroup label="Tags" options={menu.tags} selected={filters.tag} onToggle={(id) => toggleFilter('tag', id)} />
                  {filterCount > 0 && (
                    <button
                      onClick={() => setFilters(EMPTY_FILTERS)}
                      className="px-3 py-2 rounded-full text-sm bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800 transition-colors"
                    >
                      Clear All
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="px-4 py-6 space-y-6 max-w-3xl mx-auto">
        {loadError && (
          <div className="text-center py-12 space-y-4">
            <p className="text-gray-600 dark:text-gray-300">We couldn't load the menu right now.</p>
            <button onClick={load} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-500 text-white">
              <SafeIcon icon={FiRefreshCw} className="w-4 h-4" /> Try again
            </button>
          </div>
        )}

        {!menu && !loadError && <MenuSkeleton />}

        {menu && (
          <>
            {menu.pdfUrl && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden"
              >
                <a
                  href={menu.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 transition-all"
                >
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiDownload} className="w-6 h-6" />
                    <h2 className="text-xl font-bold">Download PDF Menu</h2>
                  </div>
                  <span className="text-sm bg-white/20 px-3 py-1 rounded-full hidden sm:inline">Printable Version</span>
                </a>
              </motion.div>
            )}

            {menu.houseNotes.length > 0 && (
              <Collapsible
                open={expanded.includes('addons')}
                onToggle={() => toggleSection('addons')}
                icon="ℹ️"
                title="Add-ons & Important Notes"
                gradient="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              >
                <div className="p-6 space-y-3 text-sm text-gray-600 dark:text-gray-400">
                  {menu.houseNotes.map((note, i) => (
                    <p key={i}>• {note}</p>
                  ))}
                </div>
              </Collapsible>
            )}

            {menu.happyHours.map((hh) => {
              const live = activeHappyHours.includes(hh);
              return (
                <Collapsible
                  key={`hh${hh.id}`}
                  open={live || expanded.includes(`hh${hh.id}`)}
                  onToggle={() => toggleSection(`hh${hh.id}`)}
                  icon="🎉"
                  title={hh.title}
                  gradient="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
                  badge={live && <span className="text-xs font-semibold bg-white/25 px-2 py-1 rounded-full">ON NOW</span>}
                >
                  <div className="p-6 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/30 dark:to-orange-900/30 dark:text-gray-100">
                    <p className="text-lg mb-4">{formatDays(hh.days)}, {formatTimeRange(hh.start_time, hh.end_time)}</p>
                    <div className="space-y-2">
                      {hh.specials.map((s, i) => (
                        <p key={i} className="text-lg">
                          {s.emoji} {s.price && <strong>{/^\d/.test(s.price) ? `$${s.price}` : s.price}</strong>} {s.label}
                        </p>
                      ))}
                    </div>
                    {hh.note && <p className="text-sm mt-4 opacity-90">{hh.note}</p>}
                    <button
                      onClick={() => setFilters({ ...EMPTY_FILTERS, happyHour: true })}
                      className="mt-4 text-sm font-medium text-orange-700 dark:text-orange-300 underline underline-offset-2"
                    >
                      Show eligible drinks
                    </button>
                  </div>
                </Collapsible>
              );
            })}

            {filteredCategories.map((category) => (
              <Collapsible
                key={category.id}
                open={isOpen(`c${category.id}`)}
                onToggle={() => toggleSection(`c${category.id}`)}
                icon={category.icon}
                title={category.name}
                gradient="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                badge={narrowing && <span className="text-xs bg-white/25 px-2 py-1 rounded-full">{category.drinks.length}</span>}
              >
                <div className="p-6 space-y-4">
                  {category.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">{category.description}</p>
                  )}
                  {category.drinks.map((drink, index) => {
                    const type = lookups.types.get(drink.type_id);
                    const spirits = drink.spirit_ids.map((id) => lookups.spirits.get(id)).filter(Boolean);
                    const tags = drink.tag_ids.map((id) => lookups.tags.get(id)).filter(Boolean);
                    return (
                      <motion.div
                        key={drink.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: Math.min(index, 10) * 0.05 }}
                        className="flex gap-4 items-start p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                      >
                        {drink.image_url && (
                          <img src={drink.image_url} alt="" loading="lazy" className="w-16 h-16 rounded-lg object-cover shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{drink.name}</h3>
                          {drink.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400">{drink.description}</p>
                          )}
                          {(tags.length > 0 || drink.happy_hour_eligible || type || spirits.length > 0) && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {drink.happy_hour_eligible && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200">🎉 Happy Hour</span>
                              )}
                              {tags.map((t) => (
                                <span key={t.id} className={`text-xs px-2 py-0.5 rounded-full ${tagClass(t.color)}`}>
                                  {t.icon ? `${t.icon} ` : ''}{t.name}
                                </span>
                              ))}
                              {type && (
                                <span className="text-xs px-2 py-0.5 rounded-full border border-gray-200 dark:border-gray-500 text-gray-500 dark:text-gray-300">{type.name}</span>
                              )}
                              {spirits.map((s) => (
                                <span key={s.id} className="text-xs px-2 py-0.5 rounded-full border border-gray-200 dark:border-gray-500 text-gray-500 dark:text-gray-300">{s.name}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="ml-2 text-right shrink-0">
                          <span className="text-lg font-bold text-purple-600 dark:text-purple-400">{formatPrice(drink.price)}</span>
                          {drink.price_note && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">{drink.price_note}</p>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </Collapsible>
            ))}

            {filteredCategories.length === 0 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12">
                <p className="text-gray-500 dark:text-gray-400 text-lg">No drinks found matching your search or filters.</p>
              </motion.div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-4 py-4">
        <div className="flex flex-col space-y-3">
          <div className="flex justify-center space-x-3 sm:space-x-4">
            {venue.phone && (
              <a
                href={`tel:${venue.phone}`}
                className="flex items-center space-x-2 px-5 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl transition-colors shadow-lg"
              >
                <SafeIcon icon={FiPhone} className="w-5 h-5" />
                <span className="font-medium">Call</span>
              </a>
            )}
            {venue.mapsUrl && (
              <a
                href={venue.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 px-5 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl transition-colors shadow-lg"
              >
                <SafeIcon icon={FiMapPin} className="w-5 h-5" />
                <span className="font-medium">Directions</span>
              </a>
            )}
            <button
              onClick={handleShare}
              className="flex items-center space-x-2 px-5 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-xl transition-colors shadow-lg"
            >
              <SafeIcon icon={FiShare2} className="w-5 h-5" />
              <span className="font-medium">Share</span>
            </button>
          </div>

          {venue.website && (
            <div className="flex justify-center">
              <a
                href={venue.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-full transition-colors shadow-md"
              >
                <SafeIcon icon={FiGlobe} className="w-4 h-4" />
                <span className="font-medium">Visit Our Website</span>
              </a>
            </div>
          )}
        </div>
      </footer>
    </div>
  );
}

export default Menu;
